import _get from 'lodash/get';
import React, { Fragment, useMemo } from 'react';
import { Field as FormikField, useField } from 'formik';
import { NumericFormat } from 'react-number-format';
import { ClientError, FormField } from '../../../types/general';
import * as FieldsProps from '../../../types/fields';
import './Input.scss';
import { errorMessage } from '../../Form/Field';

export type Props = FormField;

type TextFieldProps = FieldsProps.Input & FieldsProps.Number;

/**
 * Generic input type
 */
const Text: React.FC<Props> = (props) => {
	const { schema, options = {} } = props;
	const { name, fieldType, props: fieldProps } = schema;
	const { readonly, disabled, maxLength, allowedDecimals, allowNegative, range } =
		(fieldProps as TextFieldProps) || {};
	const { textMaxLength } = options;
	const [field, meta, helpers] = useField(name);
	let { value } = meta;
	let newFieldType: string = fieldType;
	let newFieldArgs: any = {};
	let renderedAs = (args: any) => <input {...args} />;
	if (fieldType === 'date') {
		value = (value || '').length === 0 ? undefined : value.substring(0, '0000-00-00'.length);
	}
	if (fieldType === 'time') {
		newFieldArgs = {
			...newFieldArgs,
			min: _get(range, 'min'),
			max: _get(range, 'max'),
		};
	}
	if (fieldType === 'datetime') {
		newFieldType = 'datetime-local';
	}
	if (fieldType === 'number') {
		newFieldType = 'text';
		value = value ? `${value}`.replace('.', ',') : '';
		let numericArgs = {
			allowNegative,
		} as any;
		if (allowedDecimals) {
			numericArgs = {
				...numericArgs,
				decimalScale: allowedDecimals,
				decimalSeparator: ',',
			};
		}
		renderedAs = (args) => (
			<NumericFormat
				{...args}
				{...numericArgs}
				onChange={(e) => e.preventDefault()}
				onValueChange={(values) => {
					const val = values.floatValue;
					helpers.setValue(val);
				}}
			/>
		);
	}
	const defaultValue = useMemo(() => {
		if (['date', 'datetime'].includes(fieldType)) {
			return undefined;
		}
		return '';
	}, [fieldType]);

	return (
		<div key={`field-${name}`} className='Field-input'>
			{!readonly && (
				<FormikField name={name}>
					{({ field, meta }: any) => (
						<Fragment>
							{renderedAs({
								className: `form-control ${meta.error && meta.touched ? 'is-invalid' : ''}`,
								type: newFieldType,
								...field,
								value: value || defaultValue,
								disabled: disabled,
								maxLength: maxLength || textMaxLength,
								...newFieldArgs,
							})}
						</Fragment>
					)}
				</FormikField>
			)}
			{meta.error && meta.touched && (
				<div className='invalid-feedback'>{errorMessage(name, meta.error as unknown as ClientError[])}</div>
			)}
			{readonly && <div className='readonly'>{value}</div>}
		</div>
	);
};

export default Text;
